import { memo, useEffect, useReducer, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { DragDropProvider } from '@dnd-kit/react'
import { useSortable, isSortable } from '@dnd-kit/react/sortable'
import { DotsSixVertical, PencilSimple, Plus } from '@phosphor-icons/react'

import { ToggleSwitch } from '@/components/ui/toggle-switch'
import { useDesignVersionToggle } from '@/hooks'

import {
  fetchAllBookingTypesOnly,
  selectBookTypesByCategory,
  sortByOrder,
  updateBookTypeOrder,
  reorderBookTypesLocally,
} from '../../../reducers/Organisation/bookTypesReducer'
import {
  createServiceQuestion,
  updateServiceQuestion,
  updateServiceQuestionOrder,
  deleteServiceQuestion,
  createServiceAnswer,
  updateServiceAnswer,
  deleteServiceAnswer,
  fetchServicesQuestions,
  selectQuestions,
} from '../../../reducers/serviceFormReducer'
import {
  selectBookCategories,
  fetchAllBookingCategories,
  editBookingCategoryOrder,
  reorderBookCategoriesLocally,
} from '../../../reducers/Organisation/bookCategoryReducer'
import { selectBuildingsForFilter } from '../../../reducers/buildingSelectorReducer'
import withAccordionControl from '../../Accordion/withAccordionControl'
import Accordion from '../../Accordion/Accordion'
import CollectionItem from '../../Collection/CollectionItem'
import OrderArrows from '../../OrderArrows/OrderArrows'

import BookingCategoryModal from './BookingCategoryModal'
import BookingTypeModal from './BookingTypeModal'
import ServiceBookingTypeItem from './ServiceBookingTypeItem'

const LEGACY_BASE_PATH = '/dashboard/organisation/bookingtypes/legacy'

const CategoryAccordionItem = memo(function CategoryAccordionItem({
  index,
  id,
  name,
  isOpen,
  onToggle,
  onEdit,
  reorderProgressing,
  onReorderUp,
  onReorderDown,
  children,
}) {
  const { t } = useTranslation(['organisation', 'common'])
  const { ref: dragRef, handleRef } = useSortable({
    id: `category-${id}`,
    index,
    disabled: isOpen,
    type: 'category',
    accept: 'category',
  })

  return (
    <Accordion
      rootRef={dragRef}
      collapsed={!isOpen}
      additionalClass={`ctp-shop-item${isOpen ? ' ctp-shop-item--open' : ''}`}
    >
      <Accordion.Head handleClick={() => onToggle(id)}>
        <CollectionItem border={false} padding={false} hover={false}>
          <span
            ref={handleRef}
            className={`ctp-shop-item__drag-handle${isOpen ? ' ctp-shop-item__drag-handle--disabled' : ''}`}
          >
            <DotsSixVertical size={20} />
          </span>
          <span className="ctp-shop-item__name ctp-shop-item__name--wrap">{name}</span>
          <span className="ctp-shop-item__category-actions">
            {isOpen ? (
              <button
                className="button button-primary ctp-shop-item__icon-button"
                aria-label={t('common:actions.edit')}
                onClick={() => onEdit(id)}
              >
                <PencilSimple size={16} />
              </button>
            ) : (
              <span className="ctp-shop-item__category-order-arrows">
                <OrderArrows
                  disabled={reorderProgressing}
                  upClick={onReorderUp}
                  downClick={onReorderDown}
                />
              </span>
            )}
          </span>
        </CollectionItem>
      </Accordion.Head>
      <Accordion.Body>{children}</Accordion.Body>
    </Accordion>
  )
})

// TODO: remove this legacy accordion page (and the Redux bookCategories / bookTypes / serviceForm slices it feeds) once the new design is fully rolled out
const OrganisationBooking = ({ accordionToggle, accordionOpened }) => {
  const { t } = useTranslation(['organisation', 'common'])
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { redirect, toggle: handleUIToggle } = useDesignVersionToggle({
    group: 'booking-types',
    screen: 'Booking Types',
    to: { path: '/dashboard/organisation/bookingtypes' },
    version: 'legacy',
  })
  const { '*': splat } = useParams()
  const [categoryId, , bookingId, mode] = (splat || '').split('/').filter(Boolean)
  const params = { categoryId, bookingId, mode }

  const bookingCategories = useSelector(selectBookCategories) || []
  const bookingTypes = useSelector(selectBookTypesByCategory) || {}
  const questions = useSelector(selectQuestions) || []
  const buildings = useSelector(selectBuildingsForFilter) || []

  const [openedCategory, setOpenedCategory] = useState(null)
  const [bookingCategoriesReorderProgressing, setBookingCategoriesReorderProgressing] =
    useState(false)
  const [bookingTypesReorderProgressing, setBookingTypesReorderProgressing] = useState(false)

  const [dragResetKey, forceDragTreeRemount] = useReducer((count) => count + 1, 0)

  useEffect(() => {
    dispatch(fetchAllBookingCategories())
    dispatch(fetchAllBookingTypesOnly())
    dispatch(fetchServicesQuestions())
  }, [dispatch])

  const toggleClick = (categoryId, typeId) => {
    accordionToggle(typeId)
    if (categoryId !== undefined) {
      setOpenedCategory((prev) => (prev === categoryId ? null : categoryId))
    }
  }

  const handleEditCategory = (categoryId) => navigate(`${LEGACY_BASE_PATH}/${categoryId}`)

  const handleModalClose = (e) => {
    e?.preventDefault()
    navigate(LEGACY_BASE_PATH)
  }

  const handleAddBookType = () => navigate(`${LEGACY_BASE_PATH}/${openedCategory}/booking/new`)

  const getNextOrderNumber = (bookingTypes) =>
    bookingTypes?.length > 0 ? Math.max(...bookingTypes.map((bt) => bt.order)) + 1 : 1

  const handleEditBookingType = (bookingType, isDuplicate) => {
    const path = isDuplicate
      ? `${LEGACY_BASE_PATH}/${openedCategory}/booking/${bookingType}/duplicate`
      : `${LEGACY_BASE_PATH}/${openedCategory}/booking/${bookingType}`
    navigate(path)
  }

  const persistBookingCategoriesOrder = (updates) => {
    if (!updates.length) return

    // Apply the new order to Redux immediately so the UI moves without waiting
    // for the network round trip; the API calls below just persist it.
    dispatch(reorderBookCategoriesLocally(updates))

    setBookingCategoriesReorderProgressing(true)
    Promise.all(updates.map(({ id, order }) => dispatch(editBookingCategoryOrder(id, order))))
      .catch((err) => {
        // A failed persist leaves the optimistic order showing something that was never
        // actually saved — re-fetch to fall back to whatever the server actually has instead
        // of silently drifting until the next unrelated refresh.
        console.warn(err)
        dispatch(fetchAllBookingCategories())
      })
      .finally(() => setBookingCategoriesReorderProgressing(false))
  }

  const reorderBookingCategories = (sortedCategories, index, direction) => {
    persistBookingCategoriesOrder(switchOrderForItems(sortedCategories, index, direction))
  }

  const persistBookingTypesOrder = (updates) => {
    if (!updates.length) return

    // Apply the new order to Redux immediately so the UI moves without waiting
    // for the network round trip; the API calls below just persist it.
    dispatch(reorderBookTypesLocally(updates))

    setBookingTypesReorderProgressing(true)
    Promise.all(updates.map(({ id, order }) => dispatch(updateBookTypeOrder(id, order))))
      .catch((err) => {
        // A failed persist leaves the optimistic order showing something that was never
        // actually saved — re-fetch to fall back to whatever the server actually has instead
        // of silently drifting until the next unrelated refresh.
        console.warn(err)
        dispatch(fetchAllBookingTypesOnly())
      })
      .finally(() => setBookingTypesReorderProgressing(false))
  }

  const reorderBookingTypes = ({ category, index, direction }) => {
    const sortedTypes = bookingTypes[category] ? sortByOrder(bookingTypes[category]) : []
    persistBookingTypesOrder(switchOrderForItems(sortedTypes, index, direction))
  }

  const sortedCategories = sortByOrder(bookingCategories)

  // Single top-level DragDropProvider for the whole categories + booking-types tree (see
  // handleDragEnd below) — nesting a separate DragDropProvider per category was unreliable:
  // @dnd-kit/react's index tracking for a *nested* sortable provider doesn't always reflect the
  // actual drop position (https://github.com/clauderic/dnd-kit/issues/1633, #1564, #1664),
  // where the library's own multi-list examples use one shared provider with per-list `group`
  // scoping instead. Routes on the dragged item's `type` (set via useSortable's `type` option).
  const handleDragEnd = (event) => {
    const { canceled, operation } = event
    if (canceled) return
    const { source } = operation
    if (!source || !isSortable(source)) return

    if (source.group !== source.initialGroup) {
      forceDragTreeRemount()
      return
    }

    const sourceIndex = source.initialIndex
    const targetIndex = source.index
    if (sourceIndex === targetIndex) return

    if (source.type === 'category') {
      const reordered = [...sortedCategories]
      const [moved] = reordered.splice(sourceIndex, 1)
      reordered.splice(targetIndex, 0, moved)
      persistBookingCategoriesOrder(computeCascadeOrder(reordered, targetIndex))
    } else if (source.type === 'bookingType') {
      const categoryId = source.group
      const categoryBookingTypes = bookingTypes[categoryId]
        ? sortByOrder(bookingTypes[categoryId])
        : []
      const reordered = [...categoryBookingTypes]
      const [moved] = reordered.splice(sourceIndex, 1)
      reordered.splice(targetIndex, 0, moved)
      persistBookingTypesOrder(computeCascadeOrder(reordered, targetIndex))
    }
  }

  if (redirect) return redirect

  return (
    <div className="ctp-service-questions">
      <div className="ctp-organisation__design-toggle">
        <ToggleSwitch
          falsyLabel={t('organisation:bookingTypes.designToggle.old')}
          truthyLabel={t('organisation:bookingTypes.designToggle.new')}
          checked={false}
          onChange={handleUIToggle}
        />
      </div>
      <div className="ctp-short-toolbar-wrapper ctp-service-questions__toolbar">
        <h2 className="ctp-service-questions__title">{t('organisation:booking.list.title')}</h2>
        <button
          className="button button-primary auto-width ctp-service-questions__add-new"
          onClick={() => navigate(`${LEGACY_BASE_PATH}/new`)}
        >
          {t('organisation:shared.actions.addNew')}
          <Plus size={16} />
        </button>
      </div>
      <div className={`ctp-shop-list${openedCategory ? ' ctp-shop-list--any-open' : ''}`}>
        {bookingCategories && (
          <DragDropProvider key={dragResetKey} onDragEnd={handleDragEnd}>
            {sortedCategories.map(({ id, name }, categoryIndex) => {
              const categoryBookingTypes = bookingTypes[id] ? sortByOrder(bookingTypes[id]) : []
              return (
                <CategoryAccordionItem
                  key={`category-${id}`}
                  index={categoryIndex}
                  id={id}
                  name={name}
                  isOpen={id === openedCategory}
                  onToggle={(categoryId) => toggleClick(categoryId, null)}
                  onEdit={handleEditCategory}
                  reorderProgressing={bookingCategoriesReorderProgressing}
                  onReorderUp={() => reorderBookingCategories(sortedCategories, categoryIndex, -1)}
                  onReorderDown={() => reorderBookingCategories(sortedCategories, categoryIndex, 1)}
                >
                  <div className="ctp-shop-list__category">
                    {categoryBookingTypes.map((bookingType, index) => (
                      <ServiceBookingTypeItem
                        key={`bookingType-${bookingType.id}`}
                        index={index}
                        bookingType={bookingType}
                        isExpanded={bookingType.id === accordionOpened}
                        onClick={() => toggleClick(undefined, bookingType.id)}
                        questions={questions.filter(
                          ({ bookingTypeId }) => bookingTypeId?.id === bookingType.id,
                        )}
                        saveQuestion={(data) => dispatch(createServiceQuestion(data))}
                        updateQuestion={(data, id) => dispatch(updateServiceQuestion(data, id))}
                        reorderQuestion={(id, order) =>
                          dispatch(updateServiceQuestionOrder(id, order)).catch((err) => {
                            // A failed persist leaves the optimistic order showing something
                            // that was never actually saved — re-fetch to fall back to
                            // whatever the server actually has instead of silently drifting
                            // until the next unrelated refresh.
                            console.warn(err)
                            dispatch(fetchServicesQuestions())
                          })
                        }
                        deleteQuestion={(id) => dispatch(deleteServiceQuestion(id))}
                        saveAnswer={(data, questionId) =>
                          dispatch(createServiceAnswer(data, questionId))
                        }
                        updateAnswer={(data, answerId, questionId) =>
                          dispatch(updateServiceAnswer(data, answerId, questionId))
                        }
                        deleteAnswer={(questionId, answerId) =>
                          dispatch(deleteServiceAnswer(questionId, answerId))
                        }
                        editBookingType={handleEditBookingType}
                        reorder={reorderBookingTypes}
                        reorderProgressing={bookingTypesReorderProgressing}
                        resetDragTree={forceDragTreeRemount}
                      />
                    ))}
                    <button
                      className="button button-primary add-new-booking"
                      onClick={handleAddBookType}
                    >
                      {t('organisation:booking.list.addBookingType')}
                    </button>
                  </div>
                </CategoryAccordionItem>
              )
            })}
          </DragDropProvider>
        )}
      </div>
      <BookingCategoryModal
        params={params}
        categoryOrder={bookingCategories.length + 1}
        onModalClose={handleModalClose}
      />
      <BookingTypeModal
        params={params}
        onModalClose={handleModalClose}
        buildings={buildings}
        bookTypeOrder={getNextOrderNumber(bookingTypes[openedCategory])}
      />
    </div>
  )
}

export default withAccordionControl(OrganisationBooking, 'multiple')

// Swaps the `order` values of two array-adjacent items (Up/Down). Works by position rather
// than by matching an exact order value, so it stays correct even once drag-and-drop
// (computeCascadeOrder below) introduces non-contiguous order values.
export const switchOrderForItems = (sortedList, currentIndex, direction) => {
  const targetIndex = currentIndex + direction
  if (currentIndex < 0 || currentIndex >= sortedList.length) return []
  if (targetIndex < 0 || targetIndex >= sortedList.length) return []

  const current = sortedList[currentIndex]
  const target = sortedList[targetIndex]
  return [
    { id: current.id, order: target.order },
    { id: target.id, order: current.order },
  ]
}

// Order updates for an item dropped at `movedIndex` in `reorderedList` (which already has the
// item spliced into its new position). Assigns the moved item a clean integer (`prev.order + 1`)
// rather than a fractional midpoint — legacy rows with duplicate/tightly-packed orders (e.g.
// neighbors 1 and 2) would otherwise get an increasingly messy value like 1.5, 1.25, ... Any
// later item that the new value collides with (or that's now out of order) gets pushed forward
// by 1 in turn, cascading only as far as needed — the list was already sorted ascending before
// the splice, so once an item is found strictly greater than the previous (possibly bumped)
// item, nothing after it can be out of order either and the cascade stops.
export const computeCascadeOrder = (reorderedList, movedIndex) => {
  const moved = reorderedList[movedIndex]
  const prev = reorderedList[movedIndex - 1]
  const next = reorderedList[movedIndex + 1]

  let order = 1
  if (prev) order = prev.order + 1
  else if (next) order = next.order - 1

  const updates = [{ id: moved.id, order }]
  let previousOrder = order
  for (let i = movedIndex + 1; i < reorderedList.length; i++) {
    const item = reorderedList[i]
    if (item.order > previousOrder) break
    previousOrder += 1
    updates.push({ id: item.id, order: previousOrder })
  }
  return updates
}
